import { Fragment, useEffect, useState } from "react";
import SimpleBar from "simplebar-react";
import { type Account, type Role, api } from "../api";
import { type Page, hashParam, hrefOf, useHash } from "./router";
import { useBooks } from "../books";
import { brandLogo } from "../brand";
import { closeDrawer, sidebarHover } from "./theme";

/**
 * The Ynex sidebar, with OHC's pages in it.
 *
 * Markup and class names are the template's own, from its starter kit, so the template
 * stylesheet does all the work: the dark menu, the category headings, the active item,
 * folding to icons on a wide screen and sliding in as a drawer on a phone.
 */

interface MenuLink {
  readonly page: Page;
  readonly title: string;
  readonly icon: string;
  /** Roles that see this item. Mirrors the server, which is the authority. */
  readonly roles?: readonly Role[];
  /** One account's view of a page: #/bank?account=... */
  readonly account?: string;
}

interface MenuSection {
  readonly title: string;
  readonly items: readonly MenuLink[];
}

const SUPERVISOR_UP: readonly Role[] = ["supervisor", "manager", "admin", "master"];
const MANAGER_UP: readonly Role[] = ["manager", "admin", "master"];

/**
 * The menu as D laid it out (15 October 2026): sales by where they were made, inventory with
 * the card buybacks, expenses with claims, each bank account's reconciliation, each account's
 * money and all of it together, then categories and reports. The account items are filled in
 * from the accounts there are, so a Wise account appears when it is added.
 */
function menuOf(accounts: readonly Account[], books: "company" | "personal"): MenuSection[] {
  const owned = accounts.filter((account) => account.isActive && (books === "personal" ? account.ownerKind === "person" : account.ownerKind === "company"));
  const banks = owned.filter((account) => account.kind === "bank");
  const money = owned.filter((account) => account.kind === "bank" || account.kind === "cash");
  return [
    {
      title: "Overview",
      items: [
        { page: "home", title: "Dashboard", icon: "bx bx-tachometer" },
        { page: "progress", title: "System development status", icon: "bx bx-home", roles: ["master"] },
        { page: "pos", title: "Sell now", icon: "bx bx-store" },
        { page: "people", title: "Staff balances", icon: "bx bx-group", roles: MANAGER_UP },
      ],
    },
    {
      title: "Sales",
      items: [
        { page: "retail", title: "Retail sales", icon: "bx bx-store-alt" },
        { page: "tradeshow", title: "Tradeshow sales", icon: "bx bx-calendar-event" },
        { page: "tiktok", title: "TikTok sales", icon: "bx bxl-tiktok" },
      ],
    },
    {
      title: "Inventory",
      items: [
        { page: "stock", title: "Inventory on hand", icon: "bx bx-box" },
        { page: "purchases", title: "Inventory purchases", icon: "bx bx-cart", roles: MANAGER_UP },
        { page: "buybacks", title: "Card buybacks", icon: "bx bx-transfer" },
      ],
    },
    {
      title: "Expenses and claims",
      items: [
        // What is bought for the business itself, not to sell: kept apart from stock (D, 11 October 2026).
        { page: "expenses", title: "Business expenses", icon: "bx bx-receipt", roles: MANAGER_UP },
        { page: "claims", title: "Expense claims", icon: "bx bx-wallet-alt", roles: MANAGER_UP },
      ],
    },
    {
      title: "Bank reconciliation",
      items: banks.map((account) => ({ page: "bank" as const, title: account.name, icon: "bx bx-building-house", roles: MANAGER_UP, account: account.id })),
    },
    {
      title: "Company accounts",
      items: money.map((account) => ({
        page: "money" as const,
        title: account.name,
        icon: account.kind === "cash" ? "bx bx-money" : "bx bx-building",
        roles: MANAGER_UP,
        account: account.id,
      })),
    },
    {
      title: "All accounts and cash",
      items: [{ page: "money", title: "All company accounts and cash", icon: "bx bx-coin-stack", roles: MANAGER_UP }],
    },
    {
      title: "Others",
      items: [{ page: "categories", title: "Transaction categories", icon: "bx bx-purchase-tag" }],
    },
    {
      title: "Reports",
      items: [{ page: "reports", title: "Reports", icon: "bx bx-line-chart", roles: MANAGER_UP }],
    },
    {
      title: "Settings",
      items: [
        { page: "accounts", title: "Financial accounts", icon: "bx bx-wallet", roles: MANAGER_UP },
        { page: "users", title: "User access", icon: "bx bx-user-check", roles: ["master"] },
        { page: "devices", title: "Devices", icon: "bx bx-devices", roles: ["master"] },
        { page: "company", title: "Company profile", icon: "bx bx-buildings", roles: ["master"] },
      ],
    },
  ];
}

/*
 * The menu is 15rem wide and the template keeps a label on one line, so the longest names
 * (System development status, Transaction categories) run past the edge at the template's
 * size. Those few are set a size smaller and may wrap, rather than being cut off.
 */
const LONG_LABEL = 20;

const LOGOS = [
  ["lockup-light", "desktop-logo"],
  ["emblem", "toggle-logo"],
  ["lockup-dark", "desktop-dark"],
  ["emblem", "toggle-dark"],
  ["lockup-dark", "desktop-white"],
  ["emblem", "toggle-white"],
] as const;

export function Sidebar({ page, role }: { readonly page: Page; readonly role: Role }): React.ReactElement {
  // What people paid for themselves never went through the company's bank, so there is
  // nothing to reconcile and no company expense in it (D, 12 October 2026).
  const books = useBooks();
  useHash();
  const [accounts, setAccounts] = useState<Account[]>([]);
  useEffect(() => {
    if (!MANAGER_UP.includes(role)) return;
    void api
      .accounts()
      .then((result) => setAccounts(result.accounts))
      .catch(() => undefined);
  }, [role]);
  const onAccount = hashParam("account");
  const notPersonal: readonly Page[] = ["bank", "expenses"];
  const sections = menuOf(accounts, books).map((section) => ({
    ...section,
    items: section.items.filter((item) => (!item.roles || item.roles.includes(role)) && !(books === "personal" && notPersonal.includes(item.page))),
  })).filter((section) => section.items.length > 0);

  return (
    <Fragment>
      <div id="responsive-overlay" onClick={() => closeDrawer()} />
      <aside
        className="app-sidebar"
        id="sidebar"
        onMouseOver={() => sidebarHover(true)}
        onMouseLeave={() => sidebarHover(false)}
      >
        <div className="main-sidebar-header">
          <a href={hrefOf("home")} className="header-logo">
            {LOGOS.map(([file, name]) => (
              <img
                key={name}
                src={brandLogo(file)}
                alt="Oh-Ho Collectibles"
                className={`main-logo ${name}`}
              />
            ))}
          </a>
        </div>

        <div className="main-sidebar" id="sidebar-scroll">
          <SimpleBar>
            <nav className="main-menu-container nav nav-pills flex-column sub-open">
              <ul className="main-menu">
                {sections.map((section) => (
                  <Fragment key={section.title}>
                    <li className="slide__category">
                      <span className="category-name">{section.title}</span>
                    </li>
                    {section.items.map((item) => {
                      const active = item.page === page && (item.account ?? "") === onAccount;
                      return (
                        <li key={`${item.page}-${item.account ?? ""}`} className={`slide ${active ? "active" : ""}`}>
                          <a
                            href={item.account ? `${hrefOf(item.page)}?account=${item.account}` : hrefOf(item.page)}
                            className={`side-menu__item ${active ? "active" : ""}`}
                            onClick={() => closeDrawer()}
                          >
                            <i className={`${item.icon} side-menu__icon`} />
                            <span
                              className={`side-menu__label ${item.title.length > LONG_LABEL ? "!text-[0.75rem] !whitespace-normal !leading-tight" : ""}`}
                            >
                              {item.title}
                            </span>
                          </a>
                        </li>
                      );
                    })}
                  </Fragment>
                ))}
              </ul>
            </nav>
          </SimpleBar>
        </div>
      </aside>
    </Fragment>
  );
}
